import React, { useEffect, useState } from "react";
import { RADAR_ATTENTION_DURATION_PATH, RADAR_ATTENTION_DURATION_SCHEMA, type RadarAttentionDurationPacketV1 } from "@shared/radarAttentionDuration";
import { COLORS } from "./radarFormat";

const LIFECYCLE_GROUPS = ["ACCELERATING", "ACTIVE", "PEAK", "EMERGING", "DECAYING", "DORMANT"] as const;
const TIER_GROUPS = ["deep", "scan", "watch"] as const;
const dispositions = ["candidate", "complete", "censored", "excluded", "unknown", "uniqueSymbols"] as const;

type DurationGroup = RadarAttentionDurationPacketV1["groups"]["lifecycle"][keyof RadarAttentionDurationPacketV1["groups"]["lifecycle"]];

function count(value: number): string {
  return value.toLocaleString("en-US");
}

function minutes(value: number): string {
  return `${(Math.round((value / 60_000) * 10) / 10).toFixed(1)} min`;
}

function titleCase(value: string): string {
  return value.replaceAll("_", " ").replaceAll(":", " · ");
}

function CountRow({ label, values }: { label: string; values: Record<string, number> }) {
  const entries = Object.entries(values);
  if (entries.length === 0) return <p style={muted}>None recorded</p>;
  return (
    <ul style={{ ...listStyle, margin: "5px 0 0" }}>
      {entries.map(([key, value]) => (
        <li key={`${label}-${key}`} style={reasonLine}>
          <span>{titleCase(key)}</span><strong>{count(value)}</strong>
        </li>
      ))}
    </ul>
  );
}

function ReasonDetails({ group }: { group: DurationGroup }) {
  return (
    <details style={detailsStyle}>
      <summary style={summaryStyle}>Disposition reasons and censor bounds</summary>
      <div style={detailGrid}>
        <section>
          <h4 style={subheading}>Candidate disposition reasons</h4>
          <CountRow label="disposition" values={group.dispositionReasonCounts} />
        </section>
        <section>
          <h4 style={subheading}>Censor classes and observed lower bounds</h4>
          <CountRow label="censor" values={group.censoring.censorTypeCounts} />
          {Object.entries(group.censoring.lowerBoundByCensorType).map(([kind, bounds]) => (
            <div key={kind} style={{ marginTop: 8 }}>
              <strong>{titleCase(kind)} bounds</strong>
              <div style={muted}>Episodes: {count(bounds.n)} · observed lower bound: {count(bounds.observedLowerBoundCount)}</div>
              <CountRow label={`${kind}-unavailable`} values={bounds.unavailableReasonCounts} />
            </div>
          ))}
          <p style={muted}>All censored episodes with observed lower bounds: {count(group.censoring.observedLowerBoundCount)}</p>
          <CountRow label="lower-unavailable" values={group.censoring.lowerBoundUnavailableReasonCounts} />
          <p style={muted}>Unknown episodes with no lower bound</p>
          <CountRow label="unknown-lower" values={group.censoring.unknownLowerBoundUnavailableReasonCounts} />
        </section>
        <section>
          <h4 style={subheading}>Earliest observed break by censor class</h4>
          {Object.entries(group.censoring.firstBreakCauseCountsByCensorType).map(([kind, reasons]) => (
            <div key={kind} style={{ marginTop: 8 }}><strong>{titleCase(kind)}</strong><CountRow label={`${kind}-break`} values={reasons} /></div>
          ))}
        </section>
        <section>
          <h4 style={subheading}>Open-at-stop break by disposition</h4>
          {Object.entries(group.censoring.openAtStopBreakCauseCountsByDisposition).map(([kind, reasons]) => (
            <div key={kind} style={{ marginTop: 8 }}><strong>{titleCase(kind)}</strong><CountRow label={`${kind}-stop`} values={reasons} /></div>
          ))}
        </section>
      </div>
    </details>
  );
}

function GroupCard({ name, group }: { name: string; group: DurationGroup }) {
  const duration = group.completeDurationMs;
  const unavailableBounds = Object.values(group.censoring.lowerBoundUnavailableReasonCounts).reduce((sum, value) => sum + value, 0);
  return (
    <article data-testid={`radar-duration-group-${name.toLowerCase()}`} style={cardStyle}>
      <h3 style={groupTitle}>{name.toUpperCase()}</h3>
      <p style={{ ...muted, marginTop: 0 }}>{group.status === "insufficient_retained_history" ? "Insufficient retained history" : "Available complete-duration summary"}</p>
      {duration ? (
        <div style={durationStyle} aria-label={`${duration.n} completed episodes; minimum ${minutes(duration.min)}, median ${minutes(duration.median)}, maximum ${minutes(duration.max)}`}>
          <div><span style={eyebrow}>Completed sample</span><strong>{count(duration.n)}</strong></div>
          <div><span style={eyebrow}>Min</span><strong>{minutes(duration.min)}</strong></div>
          <div><span style={eyebrow}>Median</span><strong>{minutes(duration.median)}</strong></div>
          <div><span style={eyebrow}>Max</span><strong>{minutes(duration.max)}</strong></div>
        </div>
      ) : (
        <p style={{ ...emptyDuration, margin: "10px 0" }}>No completed duration is available.</p>
      )}
      <dl style={countGrid}>
        {dispositions.map((key) => (
          <div key={key} style={countCell}>
            <dt style={eyebrow}>{key === "uniqueSymbols" ? "Unique symbols" : key}</dt>
            <dd style={countValue}>{count(group[key])}</dd>
          </div>
        ))}
      </dl>
      <p style={muted}>Censored lower bounds: {count(group.censoring.observedLowerBoundCount)} observed · {count(unavailableBounds)} unavailable</p>
      <div style={muted}>Repeated episode excess: {count(group.repeatedEpisodeExcess)}. Episodes are not independent.</div>
      <ReasonDetails group={group} />
    </article>
  );
}

function PopulationTotals({ packet }: { packet: RadarAttentionDurationPacketV1 }) {
  const populations = [
    ["Lifecycle total", packet.totals.lifecycle],
    ["Tier total", packet.totals.tier],
    ["Combined records", packet.totals.combined],
  ] as const;
  return (
    <div style={totalsGrid} aria-label="Historical candidate population totals">
      {populations.map(([label, total]) => (
        <div key={label} style={totalCard}>
          <strong style={groupTitle}>{label}</strong>
          <div style={muted}>Candidates {count(total.candidate)} · complete {count(total.complete)} · censored {count(total.censored)} · excluded {count(total.excluded)} · unknown {count(total.unknown)} · unique symbols {count(total.uniqueSymbols)}</div>
        </div>
      ))}
    </div>
  );
}

function Coverage({ packet }: { packet: RadarAttentionDurationPacketV1 }) {
  const { coverage } = packet;
  const facts: Array<[string, number]> = [
    ["Lifecycle transitions", coverage.sourceRows.lifecycleTransitions],
    ["Tier transitions", coverage.sourceRows.tierTransitions],
    ["Eligibility transitions", coverage.sourceRows.eligibilityTransitions],
    ["History rows", coverage.sourceRows.history1m],
    ["Valid history rows", coverage.history1m.validRows],
    ["Invalid history rows", coverage.history1m.invalidRows],
    ["Missing history bucket keys", coverage.history1m.missingBucketKeys],
    ["Raw rows", coverage.sourceRows.raw1m],
    ["Valid raw keys", coverage.raw1m.validKeys],
    ["Invalid partial-epoch raw keys", coverage.raw1m.invalidKeys],
    ["Candidates", coverage.assignmentDenominator.totalCandidates],
    ["Unique symbols", coverage.assignmentDenominator.uniqueSymbolsCombined],
    ["Left-censored episodes", coverage.leftCensored.episodes],
    ["Candidates with lower bounds", coverage.leftCensored.observedLowerBoundAvailable],
    ["Left-censored without mature clipped interval", coverage.leftCensored.noMatureClippedInterval],
    ["Left-censored without positive sample before break", coverage.leftCensored.noPositiveSampleBeforeFirstBreak],
    ["Open at stop", coverage.openAtStop.episodes],
  ];
  return <dl style={coverageGrid}>{facts.map(([label, value]) => <div key={label} style={countCell}><dt style={eyebrow}>{label}</dt><dd style={countValue}>{count(value)}</dd></div>)}</dl>;
}

function Provenance({ packet }: { packet: RadarAttentionDurationPacketV1 }) {
  const { population, provenance, coverage } = packet;
  const facts: Array<[string, string]> = [
    ["Invocation", population.invocationId],
    ["Historical Radar code commit", population.sourceCodeCommit],
    ["Campaign starting baseline commit (not the current baseline)", population.campaignStartingBaselineCommit],
    ["Protected environment SHA-256", population.protectedEnvironmentSha256],
    ["Source database SHA-256", provenance.sourceDatabaseSha256],
    ["Method SHA-256", provenance.methodologySha256],
    ["Source extraction SHA-256", provenance.sourceExtractionSha256],
    ["Corrected ledger SHA-256", provenance.correctedLedgerSha256],
    ["Audit manifest SHA-256", provenance.auditManifestSha256],
    ["Audit input bundle", provenance.auditInputBundleSha256],
    ["Feasibility review", `${provenance.independentFeasibilityReview}: ${provenance.independentReviewVerdict}`],
  ];
  return (
    <details style={detailsStyle}>
      <summary style={summaryStyle}>Provenance, coverage, and limits</summary>
      <div style={detailGrid}>
        <section>
          <h3 style={subheading}>Exact historical scope</h3>
          <p style={muted}>One fixed historical invocation. No cross-invocation pooling.</p>
          <p style={muted}>Epoch: [{population.epochStartUtcInclusive}, {population.epochEndUtcExclusive}) UTC</p>
          <p style={muted}>Mature context starts: {population.matureContextStartUtc} UTC</p>
          <p style={muted}>Observed startup settings: {count(population.startupSettingsObserved.scanPollIntervalMs / 1000)}-second scan poll, {count(population.startupSettingsObserved.watchSize)} Watch slots, {count(population.startupSettingsObserved.deepSize)} Deep slots.</p>
          <p style={muted}>Full historical process environment retained: no. Full resolved numeric configuration certified: no.</p>
          <dl style={provenanceGrid}>{facts.map(([label, value]) => <div key={label} style={provenanceItem}><dt style={eyebrow}>{label}</dt><dd style={hashValue}>{value}</dd></div>)}</dl>
          <p style={muted}>Feasibility review scope: {provenance.reviewScopeLimit}.</p>
        </section>
        <section>
          <h3 style={subheading}>Source coverage</h3>
          <Coverage packet={packet} />
          <p style={muted}>Eligibility transitions: {count(coverage.eligibilityTransitions.toIneligible)} to ineligible; {count(coverage.eligibilityTransitions.toEligible)} to eligible. Invalid raw-key reason: {titleCase(coverage.raw1m.invalidReason)}.</p>
        </section>
        <section>
          <h3 style={subheading}>Measure and limits</h3>
          <p style={muted}>{packet.measure.durationDefinition}. {packet.measure.summaryMethod}. The source worker schedule is {count(packet.measure.evaluationScheduleMs / 1000)} seconds; retained confirmation resolution is {count(packet.measure.retainedConfirmationResolutionMs / 60_000)} minute.</p>
          <p style={muted}>Displayed durations are rounded to the nearest 0.1 minute. The packet retains exact millisecond values. Complete-only summaries use episodes with ordinary endings and valid retained coverage.</p>
          <ul style={listStyle}>{packet.coverage.limitations.map((item) => <li key={item} style={{ ...muted, marginBottom: 6 }}>{item}</li>)}</ul>
          <ul style={listStyle}>{packet.limitations.map((item) => <li key={item} style={{ ...muted, marginBottom: 6 }}>{item}</li>)}</ul>
          <p style={muted}>This describes recorded assignments, not human attention or continuous market activity. It does not estimate future duration, forecast a signal, or show market direction. It does not establish current-configuration equivalence.</p>
        </section>
      </div>
    </details>
  );
}

export default function RadarAttentionDuration() {
  const [packet, setPacket] = useState<RadarAttentionDurationPacketV1 | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(RADAR_ATTENTION_DURATION_PATH, { method: "GET", credentials: "include", cache: "no-store", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Historical duration packet request failed");
        const body = await response.json() as RadarAttentionDurationPacketV1;
        if (body.schema !== RADAR_ATTENTION_DURATION_SCHEMA || body.packetId !== "e12-recorded-assignment-duration-20261014-v1") {
          throw new Error("Historical duration packet identity is invalid");
        }
        setPacket(body);
      })
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) setError(true);
      });
    return () => controller.abort();
  }, []);

  return (
    <section aria-labelledby="radar-attention-duration-title" data-testid="radar-attention-duration" style={panelStyle}>
      <h2 id="radar-attention-duration-title" style={panelTitle}>Historical recorded Radar assignment duration</h2>
      <p style={intro}>A fixed historical reference from one Radar invocation. This panel is separate from the live board and does not compare current symbols with this history.</p>
      {error && <p role="alert" style={{ ...muted, color: COLORS.bad }}>The historical duration summary is unavailable.</p>}
      {!packet && !error && <p role="status" style={muted}>Loading historical duration summary…</p>}
      {packet && (
        <>
          <p style={scopeLine}>One invocation · {packet.population.epochStartUtcInclusive} to {packet.population.epochEndUtcExclusive} UTC</p>
          <p style={muted}>Every candidate remains in its lifecycle or tier denominator. Complete durations are summarized separately from censored, excluded, and unknown assignments.</p>
          <PopulationTotals packet={packet} />
          <div style={groupGrid}>
            <section aria-labelledby="radar-duration-lifecycle-title" style={{ minWidth: 0 }}>
              <h3 id="radar-duration-lifecycle-title" style={sectionTitle}>Lifecycle</h3>
              <div style={cardGrid}>{LIFECYCLE_GROUPS.map((name) => <GroupCard key={name} name={name} group={packet.groups.lifecycle[name]} />)}</div>
            </section>
            <section aria-labelledby="radar-duration-tier-title" style={{ minWidth: 0 }}>
              <h3 id="radar-duration-tier-title" style={sectionTitle}>Attention tier</h3>
              <div style={cardGrid}>{TIER_GROUPS.map((name) => <GroupCard key={name} name={name} group={packet.groups.tier[name]} />)}</div>
            </section>
          </div>
          <Provenance packet={packet} />
        </>
      )}
    </section>
  );
}

const panelStyle: React.CSSProperties = { boxSizing: "border-box", minWidth: 0, margin: "18px 0", padding: 12, border: `1px solid ${COLORS.border}`, color: COLORS.text, background: "#0d0d0d" };
const panelTitle: React.CSSProperties = { margin: "0 0 5px", fontSize: 14, letterSpacing: "0.04em" };
const muted: React.CSSProperties = { color: COLORS.dim, fontSize: 10, lineHeight: 1.55, overflowWrap: "anywhere" };
const intro: React.CSSProperties = { ...muted, margin: "0 0 8px" };
const scopeLine: React.CSSProperties = { color: COLORS.text, fontSize: 11, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" };
const totalsGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))", gap: 6, margin: "8px 0" };
const totalCard: React.CSSProperties = { minWidth: 0, padding: 7, border: `1px solid ${COLORS.border}`, overflowWrap: "anywhere" };
const sectionTitle: React.CSSProperties = { color: COLORS.accent, fontSize: 12, margin: "10px 0 7px" };
const groupGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 460px), 1fr))", gap: 14 };
const cardGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))", gap: 8 };
const cardStyle: React.CSSProperties = { boxSizing: "border-box", minWidth: 0, padding: 9, border: `1px solid ${COLORS.border}`, background: "#101010" };
const groupTitle: React.CSSProperties = { color: COLORS.text, fontSize: 11, letterSpacing: "0.06em", margin: "0 0 3px" };
const durationStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "6px 12px", padding: "7px 0", borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` };
const eyebrow: React.CSSProperties = { display: "block", color: COLORS.dim, fontSize: 9, letterSpacing: "0.05em", textTransform: "uppercase" };
const countGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6, margin: "8px 0" };
const coverageGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 120px), 1fr))", gap: 6, margin: "8px 0" };
const countCell: React.CSSProperties = { minWidth: 0, padding: 6, border: `1px solid ${COLORS.border}` };
const countValue: React.CSSProperties = { color: COLORS.text, fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", margin: "3px 0 0" };
const emptyDuration: React.CSSProperties = { color: COLORS.warn, fontSize: 10, lineHeight: 1.5 };
const detailsStyle: React.CSSProperties = { marginTop: 8, borderTop: `1px solid ${COLORS.border}`, paddingTop: 6 };
const summaryStyle: React.CSSProperties = { color: COLORS.accent, fontSize: 10, cursor: "pointer", padding: "4px 0" };
const detailGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 10, marginTop: 6 };
const subheading: React.CSSProperties = { color: COLORS.dim, fontSize: 10, margin: "3px 0" };
const listStyle: React.CSSProperties = { listStyle: "none", padding: 0 };
const reasonLine: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, color: COLORS.dim, fontSize: 9, lineHeight: 1.5, overflowWrap: "anywhere" };
const provenanceGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))", gap: 6 };
const provenanceItem: React.CSSProperties = { minWidth: 0, border: `1px solid ${COLORS.border}`, padding: 6 };
const hashValue: React.CSSProperties = { margin: "3px 0 0", color: COLORS.text, fontSize: 9, fontFamily: "monospace", overflowWrap: "anywhere" };
